import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  ListSubheader,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Button,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  LocalFireDepartment as FireIcon,
  Landscape as LandscapeIcon,
  People as PeopleIcon,
  LocalHospital as HospitalIcon,
  FileDownload as DownloadIcon,
  Thermostat as ThermostatIcon,
  WaterDrop as HumidityIcon,
  Air as WindIcon,
  Whatshot as FdiIcon,
} from '@mui/icons-material';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  listWildfires,
  listWildfireSeasons,
  getStatsSummary,
  getMonthlyProfile,
  getFdiDistribution,
  getWeatherProfile,
  getCauseBySeason,
  getFirefighterStats,
  getMutualAidStats,
} from '../../api/wildfires';
import { exportExcel, exportJson, startSeasonalReport, getSeasonalReportJob } from '../../api/reporting';
import { listAidProvided } from '../../api/aid';

const COLORS = ['#e64a19', '#ff7d47', '#ac0800', '#2e7d32', '#60ad5e'];

// DAFF FDI category colours (Safe → Extremely Dangerous)
const FDI_COLORS = {
  Safe: '#4caf50',
  Moderate: '#ffc107',
  Dangerous: '#ff9800',
  'Very Dangerous': '#f44336',
  'Extremely Dangerous': '#7b0000',
};

const FDI_ORDER = ['Safe', 'Moderate', 'Dangerous', 'Very Dangerous', 'Extremely Dangerous'];

// Southern Hemisphere fire-season display order
const SEASON_ORDER = ['spring', 'summer', 'autumn', 'winter'];
const SEASON_LABELS = { spring: 'Spring', summer: 'Summer', autumn: 'Autumn', winter: 'Winter' };

// Sentinels for the timeframe picker. Fire-season tags (e.g. "2024/2025") use
// their raw value; the two quick filters and the "all" option use these
// reserved keys so we can branch in buildFilterParams.
const TF_ALL = '__all__';
const TF_THIS_MONTH = '__this_month__';
const TF_LAST_MONTH = '__last_month__';

const MONTH_ORDER = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Cause colour palette (up to 10 causes)
const CAUSE_COLORS = ['#e64a19', '#1976d2', '#388e3c', '#f57c00', '#7b1fa2',
                      '#0097a7', '#c62828', '#4e342e', '#37474f', '#ad1457'];

// How many firefighters to show in the top-N bar chart (the table lists all).
const FF_TOP_N = 15;
const AID_TOP_N = 12;

// Compass bearings for the wind-rose radar. The 8 principal points are always
// drawn (zero-filled) so the ring is complete; any 16-point directions present
// in the data slot in by bearing. Values outside this map (e.g. raw degrees)
// are appended untouched so no data is dropped.
const COMPASS_BEARING = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5, E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5, W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
};

// Short month + 2-digit year, e.g. "Jun '24". A 12-month window can straddle
// two calendar years, so the year is always shown.
const monthSlotLabel = (d) =>
  `${d.toLocaleString('default', { month: 'short' })} '${String(d.getFullYear()).slice(2)}`;

// Build `count` consecutive month slots starting at (startYear, startMonthIdx0).
// Uses the Date constructor's overflow/underflow normalisation, so a negative
// or >11 start index rolls the year correctly (used for rolling last-12).
const buildMonthSlots = (startYear, startMonthIdx0, count) => {
  const slots = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(startYear, startMonthIdx0 + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    slots.push({ key, label: monthSlotLabel(d) });
  }
  return slots;
};

// Turn a "YYYY-MM" key into a display label (for the quick-filter fallback).
const formatMonthKey = (key) => {
  const [y, m] = String(key).split('-').map(Number);
  return monthSlotLabel(new Date(y, (m || 1) - 1, 1));
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box>{children}</Box> : null;
}

const StatsPage = () => {
  const { tenantId } = useTenant();
  const { can } = useAuth();
  const canExport = can('reporting', 'export');

  // ── shared filter state ──────────────────────────────────────────────── //
  const [activeTab, setActiveTab] = useState(0);
  const [groupBy, setGroupBy] = useState('month');

  // Timeframe picker. Mirrors the season dropdown on the Map page (sourced
  // from /wildfires/seasons), plus two quick-filter entries for "This Month"
  // and "Last Month". Default seeded from current_season once loaded.
  const [timeframe, setTimeframe] = useState(TF_ALL);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [seasonsLoaded, setSeasonsLoaded] = useState(false);
  // Season-window shape (from /wildfires/seasons) — drives the Overview's
  // 12-month band when a fire season is selected.
  const [fireSeasonCrossYear, setFireSeasonCrossYear] = useState(false);
  const [fireSeasonStartMonth, setFireSeasonStartMonth] = useState(1);

  // ── tab 0: overview ──────────────────────────────────────────────────── //
  const [stats, setStats] = useState(null);
  const [wildfires, setWildfires] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── tab 1: seasonal ──────────────────────────────────────────────────── //
  const [monthlyProfile, setMonthlyProfile] = useState(null);
  const [seasonStats, setSeasonStats] = useState(null);
  const [yoyStats, setYoyStats] = useState(null);
  const [seasonLoading, setSeasonLoading] = useState(false);

  // ── tab 2: FDI & weather ─────────────────────────────────────────────── //
  const [fdiDistribution, setFdiDistribution] = useState(null);
  const [weatherProfile, setWeatherProfile] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // ── tab 3: cause analysis ────────────────────────────────────────────── //
  const [causeBySeasonData, setCauseBySeasonData] = useState(null);
  const [causeLoading, setCauseLoading] = useState(false);

  // ── tab 4: firefighters ──────────────────────────────────────────────── //
  const [firefighterStats, setFirefighterStats] = useState(null);
  const [firefighterLoading, setFirefighterLoading] = useState(false);

  // Mutual aid (tab 5). Two directions, two sources: `mutualAidStats` reads
  // this FPA own fires, while `aidProvided` reads the receipt rows written
  // when another FPA credited us. They cannot share an endpoint, because an
  // FPA cannot query another FPA fires.
  const [mutualAidStats, setMutualAidStats] = useState(null);
  const [aidProvided, setAidProvided] = useState(null);
  const [aidLoading, setAidLoading] = useState(false);

  // ── export ───────────────────────────────────────────────────────────── //
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingJson, setIsExportingJson] = useState(false);
  const [isExportingSeasonal, setIsExportingSeasonal] = useState(false);
  const [seasonalProgress, setSeasonalProgress] = useState(0);

  // ── filter helpers ───────────────────────────────────────────────────── //
  // Turns the selected timeframe into request params. Fire-season tags go
  // through `fire_season`; the month quick filters compute a UTC window and
  // pass it as start/end. When the user has picked a Group By that is itself
  // a season tag, the month-range path skips the date params (would conflict).
  const buildFilterParams = useCallback(() => {
    const isTagGrouping = groupBy === 'fire_season' || groupBy === 'financial_year';
    const p = {};

    if (timeframe === TF_THIS_MONTH || timeframe === TF_LAST_MONTH) {
      if (!isTagGrouping) {
        const now = new Date();
        const offset = timeframe === TF_LAST_MONTH ? 1 : 0;
        const start = new Date(now.getFullYear(), now.getMonth() - offset, 1);
        const end =
          timeframe === TF_LAST_MONTH
            ? new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
            : now;
        p.start_date = start.toISOString();
        p.end_date = end.toISOString();
      }
    } else if (timeframe !== TF_ALL) {
      p.fire_season = timeframe;
    }

    if (tenantId) p.tenant_id = tenantId;
    return { params: p, isTagGrouping };
  }, [groupBy, timeframe, tenantId]);

  // ── fetch overview (tab 0) ───────────────────────────────────────────── //
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { params, isTagGrouping } = buildFilterParams();
        const summaryParams = { ...params, group_by: groupBy };
        const listParams = { page: 1, page_size: 10, ...params };

        const [d, wildfiresData] = await Promise.all([
          getStatsSummary(summaryParams),
          listWildfires(listParams),
        ]);
        setStats({
          summary: {
            total_wildfires: d.total_wildfires,
            total_area_burnt: d.total_area_burnt,
            total_man_hours: d.total_man_hours,
            total_injuries: d.total_injuries,
          },
          // Keep the raw keyed dicts; the ordered/zero-filled axis is derived
          // in `overviewChartData` so it can react to the timeframe + config.
          wildfires_by_month: d.wildfires_by_month || {},
          area_burnt_by_month: d.area_burnt_by_month || {},
        });
        setWildfires(wildfiresData.wildfires);
      } catch (err) {
        console.error('Error fetching overview data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [groupBy, timeframe, tenantId, buildFilterParams]);

  // ── fetch seasonal data (tab 1) ──────────────────────────────────────── //
  useEffect(() => {
    if (activeTab !== 1) return;
    const fetchSeasonal = async () => {
      setSeasonLoading(true);
      try {
        const { params } = buildFilterParams();
        const [monthlyData, seasonData, yoyData] = await Promise.all([
          getMonthlyProfile(params),
          getStatsSummary({ ...params, group_by: 'season' }),
          getStatsSummary({ group_by: 'fire_season' }),
        ]);
        setMonthlyProfile(monthlyData.monthly_profile);
        setSeasonStats(seasonData);
        setYoyStats(yoyData);
      } catch (err) {
        console.error('Error fetching seasonal data:', err);
      } finally {
        setSeasonLoading(false);
      }
    };
    fetchSeasonal();
  }, [activeTab, timeframe, tenantId, buildFilterParams]);

  // ── fetch FDI / weather data (tab 2) ─────────────────────────────────── //
  useEffect(() => {
    if (activeTab !== 2) return;
    const fetchWeather = async () => {
      setWeatherLoading(true);
      try {
        const { params } = buildFilterParams();
        const [fdiData, weatherData] = await Promise.all([
          getFdiDistribution(params),
          getWeatherProfile(params),
        ]);
        setFdiDistribution(fdiData);
        setWeatherProfile(weatherData);
      } catch (err) {
        console.error('Error fetching weather data:', err);
      } finally {
        setWeatherLoading(false);
      }
    };
    fetchWeather();
  }, [activeTab, timeframe, tenantId, buildFilterParams]);

  // ── fetch cause data (tab 3) ─────────────────────────────────────────── //
  useEffect(() => {
    if (activeTab !== 3) return;
    const fetchCauses = async () => {
      setCauseLoading(true);
      try {
        const { params } = buildFilterParams();
        const causeData = await getCauseBySeason(params);
        setCauseBySeasonData(causeData.cause_by_season);
      } catch (err) {
        console.error('Error fetching cause data:', err);
      } finally {
        setCauseLoading(false);
      }
    };
    fetchCauses();
  }, [activeTab, timeframe, tenantId, buildFilterParams]);

  // ── fetch mutual aid, both directions (tab 5) ────────────────────────── //
  useEffect(() => {
    if (activeTab !== 5) return;
    const fetchAid = async () => {
      setAidLoading(true);
      try {
        const { params } = buildFilterParams();
        // Settled independently: "aid we provided" 400s for a super admin on
        // "All FPAs" (receipts are partitioned by provider, so there is no
        // unscoped answer), and that must not blank the received panel too.
        const [received, provided] = await Promise.allSettled([
          getMutualAidStats(params),
          listAidProvided(params),
        ]);
        if (received.status === 'rejected') {
          console.error('Error fetching assistance received:', received.reason);
        }
        // A 400 here is expected on "All FPAs" and is not worth logging as an
        // error; anything else is.
        if (provided.status === 'rejected' && provided.reason?.response?.status !== 400) {
          console.error('Error fetching aid provided:', provided.reason);
        }
        setMutualAidStats(received.status === 'fulfilled' ? received.value : null);
        setAidProvided(provided.status === 'fulfilled' ? provided.value : null);
      } catch (err) {
        console.error('Error fetching mutual aid data:', err);
      } finally {
        setAidLoading(false);
      }
    };
    fetchAid();
  }, [activeTab, timeframe, tenantId, buildFilterParams]);

  // ── fetch firefighter stats (tab 4) ──────────────────────────────────── //
  useEffect(() => {
    if (activeTab !== 4) return;
    const fetchFirefighters = async () => {
      setFirefighterLoading(true);
      try {
        const { params } = buildFilterParams();
        const data = await getFirefighterStats(params);
        setFirefighterStats(data);
      } catch (err) {
        console.error('Error fetching firefighter data:', err);
      } finally {
        setFirefighterLoading(false);
      }
    };
    fetchFirefighters();
  }, [activeTab, timeframe, tenantId, buildFilterParams]);

  // ── load season list (shares /wildfires/seasons with the Map page) ───── //
  // Seeds the timeframe picker with the current season when the FPA has fires
  // for it; otherwise leaves "All seasons" selected. Re-runs on tenant
  // switch so each FPA shows its own seasons.
  useEffect(() => {
    let cancelled = false;
    setSeasonsLoaded(false);
    (async () => {
      try {
        const params = {};
        if (tenantId) params.tenant_id = tenantId;
        const data = await listWildfireSeasons(params);
        if (cancelled) return;
        setSeasonOptions(data.seasons || []);
        setCurrentSeason(data.current_season ?? null);
        setFireSeasonCrossYear(Boolean(data.fire_season_cross_year));
        setFireSeasonStartMonth(data.fire_season_start_month || 1);
        const currentInList =
          data.current_season && (data.seasons || []).includes(data.current_season);
        setTimeframe(currentInList ? data.current_season : TF_ALL);
      } catch (err) {
        console.error('Failed to load wildfire seasons:', err);
        if (!cancelled) {
          setSeasonOptions([]);
          setCurrentSeason(null);
          setTimeframe(TF_ALL);
        }
      } finally {
        if (!cancelled) setSeasonsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Forward the active tenant + on-screen filters so the export matches
      // what the user is looking at. The backend now rejects tenant_id=None
      // for super-admins, so this is also load-bearing.
      const { params } = buildFilterParams();
      const response = await exportExcel({ ...params, year: exportYear, month: exportMonth });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = new Date(exportYear, exportMonth - 1, 1).toLocaleString('default', { month: 'long' });
      link.setAttribute('download', `wildfires_${monthName.toLowerCase()}_${exportYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Failed to export data. Please try again later.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleJsonExport = async () => {
    setIsExportingJson(true);
    try {
      const { params } = buildFilterParams();
      const response = await exportJson(params);
      // axios is configured with responseType: 'blob', so response.data is
      // already a Blob containing the server's JSON bytes — wrap it
      // directly. (Previously this ran JSON.stringify(blob) which produced
      // a literal "{}" download.)
      const dataBlob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `wildfires_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Failed to export JSON data. Please try again later.');
    } finally {
      setIsExportingJson(false);
    }
  };

  // Full Seasonal Overview PDF — scoped to the selected fire-season tag (not the
  // "all seasons" / month quick filters, which have no single season to report).
  const seasonTagSelected =
    timeframe !== TF_ALL && timeframe !== TF_THIS_MONTH && timeframe !== TF_LAST_MONTH;

  // Queue the render, then poll. The report takes ~26s and API Gateway hangs up
  // at 30s, so it cannot be produced inside one request; the server hands it to
  // a background Lambda and we wait for the job to report a download URL.
  const POLL_INTERVAL_MS = 2000;
  const POLL_TIMEOUT_MS = 10 * 60 * 1000;

  const handleSeasonalExport = async () => {
    if (!seasonTagSelected) return;
    setError(null);
    setSeasonalProgress(0);
    setIsExportingSeasonal(true);
    try {
      const params = { fire_season: timeframe };
      if (tenantId) params.tenant_id = tenantId;

      const { data: started } = await startSeasonalReport(params);
      const jobId = started?.job_id;
      if (!jobId) throw new Error('The server did not return a report job.');

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) {
          throw new Error('The report is taking longer than expected. It may still finish — try again shortly.');
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const { data: job } = await getSeasonalReportJob(jobId);
        if (typeof job?.progress === 'number') setSeasonalProgress(job.progress);

        if (job?.status === 'failed') {
          throw new Error(job.error || 'The report failed to render.');
        }
        if (job?.status === 'completed') {
          const url = job?.download?.url;
          if (!url) throw new Error('The report finished but no download was available.');
          // S3 serves it with Content-Disposition: attachment, so a plain
          // navigation downloads it under the right name — no cross-origin
          // fetch of the bucket needed.
          const link = document.createElement('a');
          link.href = url;
          link.rel = 'noopener';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          break;
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(detail || 'Failed to generate the seasonal report. Please try again later.');
    } finally {
      setIsExportingSeasonal(false);
      setSeasonalProgress(0);
    }
  };


  // ── derived chart data helpers ───────────────────────────────────────── //
  // Overview trends — ordered, zero-filled, oldest-left → newest-right.
  // Month grouping always renders a 12-month window (a selected season's band,
  // or a rolling last-12 when "All seasons"); other groupings sort ascending.
  const overviewChartData = useMemo(() => {
    if (!stats) return [];
    const counts = stats.wildfires_by_month || {};
    const areas = stats.area_burnt_by_month || {};
    const toRow = ({ key, label }) => ({
      name: label,
      wildfires: counts[key] ?? 0,
      area: parseFloat((areas[key] ?? 0).toFixed(1)),
    });

    // Non-month groupings: keys are "YYYY" or "YYYY/YYYY" tags, whose
    // lexicographic order is already chronological.
    if (groupBy !== 'month') {
      return Object.keys(counts)
        .sort()
        .map((k) => toRow({ key: k, label: k }));
    }

    const isQuick = timeframe === TF_THIS_MONTH || timeframe === TF_LAST_MONTH;
    const isSeasonTag = timeframe !== TF_ALL && !isQuick;

    let slots;
    if (isSeasonTag) {
      // Fire-season band: cross-year seasons start at the configured month;
      // per-year seasons are a plain Jan–Dec calendar year.
      const startYear = parseInt(String(timeframe).slice(0, 4), 10);
      const startMonthIdx0 = fireSeasonCrossYear ? fireSeasonStartMonth - 1 : 0;
      slots = Number.isNaN(startYear) ? [] : buildMonthSlots(startYear, startMonthIdx0, 12);
    } else if (timeframe === TF_ALL) {
      const now = new Date();
      slots = buildMonthSlots(now.getFullYear(), now.getMonth() - 11, 12);
    } else {
      // This/Last month quick filter — respect the narrow window.
      slots = Object.keys(counts)
        .sort()
        .map((key) => ({ key, label: formatMonthKey(key) }));
    }
    return slots.map(toRow);
  }, [stats, groupBy, timeframe, fireSeasonCrossYear, fireSeasonStartMonth]);

  // Wind-rose radar data: the COMPLETE 16-point ring, zero-filled, clockwise
  // from N.
  //
  // The full ring is not cosmetic. RadarChart spaces its data evenly by INDEX,
  // so the ring's position and the true bearing are only the same thing when
  // every point is present. Building it from the 8 principals plus whichever
  // 16-point directions happened to occur gives a ragged list — a season with
  // three recorded directions yields 11 entries, and the prevailing NNE gets
  // drawn at 32.7° instead of 22.5°. A season that happens to record all 16
  // renders correctly, which is exactly why this went unnoticed.
  //
  // Directions that are not compass points have no bearing to be drawn at, and
  // appending them (as this used to) shifted every sector after them, so they
  // are dropped. Mirrors seasonal_report._windrose_data on the backend.
  const windRoseData = useMemo(() => {
    const freq = weatherProfile?.wind_direction_frequency || {};
    const norm = {};
    Object.entries(freq).forEach(([k, v]) => {
      const key = String(k).trim().toUpperCase();
      if (key in COMPASS_BEARING) norm[key] = (norm[key] || 0) + Number(v || 0);
    });
    return Object.keys(COMPASS_BEARING)
      .sort((a, b) => COMPASS_BEARING[a] - COMPASS_BEARING[b])
      .map((dir) => ({ direction: dir, fires: norm[dir] || 0 }));
  }, [weatherProfile]);

  const firefighterList = firefighterStats?.firefighters ?? [];
  const topFirefighters = firefighterList
    .slice(0, FF_TOP_N)
    .map((f) => ({ ...f, name: f.name || 'Unknown' }));

  const aidProviders = mutualAidStats?.providers ?? [];
  const aidTotals = mutualAidStats?.totals ?? null;
  const topAidProviders = aidProviders
    .slice(0, AID_TOP_N)
    .map((p) => ({ ...p, name: p.name || 'Unknown' }));
  const aidReceipts = aidProvided?.receipts ?? [];
  const providedTotals = aidProvided?.totals ?? null;

  const monthlyChartData = monthlyProfile
    ? MONTH_ORDER.map((m) => ({
        name: m,
        fires: monthlyProfile[m]?.count ?? 0,
        area: parseFloat((monthlyProfile[m]?.area_ha ?? 0).toFixed(1)),
      }))
    : [];

  const seasonChartData = seasonStats
    ? SEASON_ORDER.filter((s) => seasonStats.wildfires_by_month?.[s] !== undefined || true).map((s) => ({
        name: SEASON_LABELS[s] || s,
        fires: seasonStats.wildfires_by_month?.[s] ?? 0,
        area: parseFloat((seasonStats.area_burnt_by_month?.[s] ?? 0).toFixed(1)),
      }))
    : [];

  const yoyChartData = yoyStats
    ? Object.keys(yoyStats.wildfires_by_month || {}).sort().map((key) => ({
        name: key,
        fires: yoyStats.wildfires_by_month[key] ?? 0,
        area: parseFloat((yoyStats.area_burnt_by_month?.[key] ?? 0).toFixed(1)),
      }))
    : [];

  const fdiPieData = fdiDistribution
    ? FDI_ORDER.filter((cat) => fdiDistribution.fdi_distribution?.[cat] > 0).map((cat) => ({
        name: cat,
        value: fdiDistribution.fdi_distribution[cat],
        color: FDI_COLORS[cat],
      }))
    : [];

  const weatherBySeasonData = weatherProfile
    ? SEASON_ORDER.filter((s) => weatherProfile.by_season?.[s]).map((s) => ({
        name: SEASON_LABELS[s] || s,
        avgTemp: parseFloat((weatherProfile.by_season[s].avg_temp ?? 0).toFixed(1)),
        avgFdi: parseFloat((weatherProfile.by_season[s].avg_fdi ?? 0).toFixed(1)),
        fires: weatherProfile.by_season[s].fire_count ?? 0,
      }))
    : [];

  // Flatten cause-by-season into a chart-friendly format
  const buildCausePieData = () => {
    if (!causeBySeasonData) return [];
    const totals = {};
    Object.values(causeBySeasonData).forEach((causes) => {
      Object.entries(causes).forEach(([cause, count]) => {
        totals[cause] = (totals[cause] || 0) + count;
      });
    });
    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value], i) => ({ name, value, color: CAUSE_COLORS[i % CAUSE_COLORS.length] }));
  };

  const buildCauseBySeasonChartData = () => {
    if (!causeBySeasonData) return { data: [], causes: [] };
    const allCauses = new Set();
    Object.values(causeBySeasonData).forEach((c) => Object.keys(c).forEach((k) => allCauses.add(k)));
    const causes = [...allCauses].slice(0, 5);
    const data = SEASON_ORDER.filter((s) => causeBySeasonData[s]).map((s) => {
      const row = { name: SEASON_LABELS[s] || s };
      causes.forEach((c) => { row[c] = causeBySeasonData[s]?.[c] ?? 0; });
      return row;
    });
    return { data, causes };
  };

  const causePieData = buildCausePieData();
  const { data: causeSeasonData, causes: causeList } = buildCauseBySeasonChartData();

  // ── render ───────────────────────────────────────────────────────────── //
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Wildfire Statistics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Explore data and trends about wildfire incidents.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Shared filter strip — Season dropdown mirrors the Map page picker,
          with "This Month" / "Last Month" as quick-filter entries. */}
      <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControl sx={{ minWidth: { xs: 180, sm: 240 } }} disabled={!seasonsLoaded}>
            <InputLabel id="timeframe-label">Season</InputLabel>
            <Select
              labelId="timeframe-label"
              value={timeframe}
              label="Season"
              onChange={(e) => setTimeframe(e.target.value)}
            >
              <MenuItem value={TF_ALL}>All seasons</MenuItem>
              {seasonOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                  {s === currentSeason ? ' · current' : ''}
                </MenuItem>
              ))}
              {currentSeason && !seasonOptions.includes(currentSeason) && (
                <MenuItem value={currentSeason}>
                  {currentSeason} · current (no fires yet)
                </MenuItem>
              )}
              <ListSubheader>Quick filters</ListSubheader>
              <MenuItem value={TF_THIS_MONTH}>This Month</MenuItem>
              <MenuItem value={TF_LAST_MONTH}>Last Month</MenuItem>
            </Select>
          </FormControl>

          {/* Group-by only affects the Overview tab charts */}
          {activeTab === 0 && (
            <FormControl sx={{ minWidth: { xs: 130, sm: 180 } }}>
              <InputLabel id="group-by-label">Group By</InputLabel>
              <Select
                labelId="group-by-label"
                value={groupBy}
                label="Group By"
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="year">Calendar Year</MenuItem>
                <MenuItem value="fire_season">Fire Season</MenuItem>
                <MenuItem value="financial_year">Financial Year</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </Paper>

      {/* Tab navigation */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Overview" />
          <Tab label="Seasonal Analysis" />
          <Tab label="FDI & Weather" />
          <Tab label="Cause Analysis" />
          <Tab label="Firefighters" />
          <Tab label="Mutual Aid" />
        </Tabs>
      </Paper>

      {/* ── TAB 0: OVERVIEW ────────────────────────────────────────────── */}
      <TabPanel value={activeTab} index={0}>
        {/* Export section */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Export Wildfire Data</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <FormControl sx={{ minWidth: 100 }}>
              <InputLabel id="export-year-label">Year</InputLabel>
              <Select labelId="export-year-label" value={exportYear} label="Year" onChange={(e) => setExportYear(e.target.value)}>
                {[...Array(5)].map((_, i) => {
                  const y = new Date().getFullYear() - i;
                  return <MenuItem key={y} value={y}>{y}</MenuItem>;
                })}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="export-month-label">Month</InputLabel>
              <Select labelId="export-month-label" value={exportMonth} label="Month" onChange={(e) => setExportMonth(e.target.value)}>
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {canExport && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  disabled={isExporting || !tenantId}
                  title={!tenantId ? 'Pick a tenant in the top bar before exporting — the workbook is per-FPA.' : ''}
                >
                  {isExporting ? 'Exporting...' : 'Export to Excel'}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={handleJsonExport}
                  disabled={isExportingJson || !tenantId}
                  title={!tenantId ? 'Pick a tenant in the top bar before exporting.' : ''}
                >
                  {isExportingJson ? 'Exporting...' : 'Export to JSON'}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleSeasonalExport}
                  disabled={isExportingSeasonal || !tenantId || !seasonTagSelected}
                  title={
                    !tenantId
                      ? 'Pick a tenant in the top bar before generating the report.'
                      : !seasonTagSelected
                        ? 'Select a fire season above — the seasonal report covers one season.'
                        : ''
                  }
                >
                  {isExportingSeasonal
                    ? (seasonalProgress > 0
                        ? `Generating… ${Math.round(seasonalProgress * 100)}%`
                        : 'Generating…')
                    : 'Seasonal Report (PDF)'}
                </Button>
              </>
            )}
          </Box>
        </Paper>


        {stats && (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {[
                { icon: <FireIcon color="primary" />, label: 'Wildfires', value: stats.summary.total_wildfires, sub: 'Resolved wildfires only' },
                { icon: <LandscapeIcon color="primary" />, label: 'Area Burnt', value: stats.summary.total_area_burnt.toLocaleString(), sub: 'Hectares' },
                {
                  icon: <PeopleIcon color="primary" />,
                  label: 'Man Hours',
                  value: stats.summary.total_man_hours.toLocaleString(),
                  // Own crew stays the headline so season-on-season comparisons
                  // against pre-mutual-aid numbers still hold; assistance is
                  // called out beneath rather than folded in.
                  sub: stats.summary.mutual_aid_man_hours
                    ? `Own crew · +${stats.summary.mutual_aid_man_hours.toLocaleString()} assisting = ${(
                        stats.summary.total_man_hours + stats.summary.mutual_aid_man_hours
                      ).toLocaleString()}`
                    : 'Hours',
                },
                { icon: <HospitalIcon color="primary" />, label: 'Injuries', value: stats.summary.total_injuries.toLocaleString(), sub: 'Reported' },
              ].map(({ icon, label, value, sub }) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>{icon}<Typography variant="h6" sx={{ ml: 1 }}>{label}</Typography></Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                      <Typography variant="body2" color="text.secondary">{sub}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Wildfire Trends</Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>Number of Wildfires</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={overviewChartData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="wildfires" name="Wildfires" fill="#e64a19" />
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>Area Burnt (Hectares)</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={overviewChartData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="area" name="Area Burnt" stroke="#e64a19" activeDot={{ r: 8 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>Recent Wildfires</Typography>
              <Divider sx={{ mb: 2 }} />
              {wildfires.length > 0 ? (
                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Area (Hectares)</TableCell>
                        <TableCell>Duration (Hours)</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {wildfires.map((wf) => (
                        <TableRow key={wf.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell>{wf.id}</TableCell>
                          <TableCell>{wf.start_time ? new Date(wf.start_time).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>
                            {wf.is_active
                              ? <Box sx={{ color: 'error.main', fontWeight: 'bold' }}>Active</Box>
                              : <Box sx={{ color: 'success.main' }}>Resolved</Box>}
                          </TableCell>
                          <TableCell>{wf.area_size_hectares?.toLocaleString() || 'N/A'}</TableCell>
                          <TableCell>{wf.duration_hours?.toLocaleString() || 'N/A'}</TableCell>
                          <TableCell>
                            <Button variant="contained" color="primary" size="small" component={RouterLink} to={`/wildfires/${wf.id}`}>View Details</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No wildfires found for the selected time period.</Alert>
              )}
            </Paper>
          </>
        )}
      </TabPanel>

      {/* ── TAB 1: SEASONAL ANALYSIS ────────────────────────────────────── */}
      <TabPanel value={activeTab} index={1}>
        {seasonLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            {/* Monthly Fire Profile */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Monthly Fire Profile (Jan–Dec)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total fires and area burnt per calendar month across the selected filter scope.
              </Typography>
              {monthlyChartData.every((d) => d.fires === 0) ? (
                <Alert severity="info">No data for the selected period.</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={monthlyChartData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" orientation="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="fires" name="Fires" fill="#e64a19" />
                    <Bar yAxisId="right" dataKey="area" name="Area (ha)" fill="#1976d2" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>

            {/* Fire Activity by Season */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Fire Activity by Season</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Southern Hemisphere seasons — Spring (Sep–Nov), Summer (Dec–Feb), Autumn (Mar–May), Winter (Jun–Aug).
              </Typography>
              {seasonChartData.every((d) => d.fires === 0) ? (
                <Alert severity="info">No seasonal data available for the selected period.</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={seasonChartData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" orientation="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="fires" name="Fires" fill="#e64a19" />
                    <Bar yAxisId="right" dataKey="area" name="Area (ha)" fill="#ff9800" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>

            {/* Year-over-Year Fire Season Comparison */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>Year-over-Year Fire Season Comparison</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Fires and area burnt per fire season tag across all years. Useful for annual trend reporting.
              </Typography>
              {yoyChartData.length === 0 ? (
                <Alert severity="info">No fire season data found. Tag wildfires with a fire season to enable this chart.</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={yoyChartData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" orientation="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="fires" name="Fires" fill="#ac0800" />
                    <Bar yAxisId="right" dataKey="area" name="Area (ha)" fill="#2e7d32" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </>
        )}
      </TabPanel>

      {/* ── TAB 2: FDI & WEATHER ────────────────────────────────────────── */}
      <TabPanel value={activeTab} index={2}>
        {weatherLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            {/* Weather KPI cards */}
            {weatherProfile?.overall && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {[
                  { icon: <ThermostatIcon sx={{ color: '#e64a19' }} />, label: 'Avg Temp at Ignition', value: `${weatherProfile.overall.avg_temp.toFixed(1)}°C`, sub: 'Average temperature' },
                  { icon: <HumidityIcon sx={{ color: '#1976d2' }} />, label: 'Avg Humidity', value: `${weatherProfile.overall.avg_humidity.toFixed(1)}%`, sub: 'Average relative humidity' },
                  { icon: <WindIcon sx={{ color: '#388e3c' }} />, label: 'Avg Wind Speed', value: `${weatherProfile.overall.avg_wind_kmh.toFixed(1)} km/h`, sub: 'Average wind at ignition' },
                  { icon: <FdiIcon sx={{ color: '#ac0800' }} />, label: 'Avg FDI', value: weatherProfile.overall.avg_fdi.toFixed(1), sub: `Based on ${weatherProfile.total_with_weather_data} fires with weather data` },
                ].map(({ icon, label, value, sub }) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>{icon}<Typography variant="h6" sx={{ ml: 1 }}>{label}</Typography></Box>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                        <Typography variant="body2" color="text.secondary">{sub}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* FDI Distribution Pie */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom>FDI Distribution at Ignition</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    At what fire danger level did fires start? Fires without weather data are excluded.
                  </Typography>
                  {fdiPieData.length === 0 ? (
                    <Alert severity="info">No FDI data available. Import fires with weather data to enable this chart.</Alert>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={fdiPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {fdiPieData.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => [v, 'Fires']} />
                        </PieChart>
                      </ResponsiveContainer>
                      {fdiDistribution && (
                        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                          {fdiDistribution.total_with_weather_data} of {fdiDistribution.total_wildfires_in_filter} fires have weather data · Avg FDI: {fdiDistribution.avg_fdi?.toFixed(1)}
                        </Typography>
                      )}
                    </>
                  )}
                </Paper>
              </Grid>

              {/* Weather conditions by season */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom>Avg Temperature &amp; FDI by Season</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Average temperature and FDI at ignition per meteorological season.
                  </Typography>
                  {weatherBySeasonData.length === 0 ? (
                    <Alert severity="info">No weather data by season available.</Alert>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={weatherBySeasonData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="temp" orientation="left" label={{ value: '°C', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="fdi" orientation="right" label={{ value: 'FDI', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="temp" dataKey="avgTemp" name="Avg Temp (°C)" fill="#ff9800" />
                        <Bar yAxisId="fdi" dataKey="avgFdi" name="Avg FDI" fill="#ac0800" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Wind direction wind-rose */}
            {weatherProfile?.wind_direction_frequency && Object.keys(weatherProfile.wind_direction_frequency).length > 0 && (
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>Wind Direction at Ignition</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Frequency of wind directions recorded at the time of fire ignition, arranged as a compass wind rose.
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <RadarChart data={windRoseData} outerRadius="75%">
                    <PolarGrid />
                    <PolarAngleAxis dataKey="direction" />
                    <PolarRadiusAxis angle={90} allowDecimals={false} />
                    <Radar name="Fires" dataKey="fires" stroke="#1976d2" fill="#1976d2" fillOpacity={0.5} />
                    <Tooltip formatter={(v) => [v, 'Fires']} />
                  </RadarChart>
                </ResponsiveContainer>
              </Paper>
            )}
          </>
        )}
      </TabPanel>

      {/* ── TAB 3: CAUSE ANALYSIS ───────────────────────────────────────── */}
      <TabPanel value={activeTab} index={3}>
        {causeLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Overall cause distribution pie */}
              <Grid item xs={12} md={5}>
                <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom>Overall Cause Distribution</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Top causes aggregated across all seasons.
                  </Typography>
                  {causePieData.length === 0 ? (
                    <Alert severity="info">No cause data available for the selected period.</Alert>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={causePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={({ name, percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}>
                          {causePieData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, name) => [v, name]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Paper>
              </Grid>

              {/* Cause by season grouped bar */}
              <Grid item xs={12} md={7}>
                <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom>Cause by Season (Top 5)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Dominant causes per meteorological season — drives prevention campaigns and patrol planning.
                  </Typography>
                  {causeSeasonData.length === 0 ? (
                    <Alert severity="info">No cause-by-season data available.</Alert>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={causeSeasonData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {causeList.map((cause, i) => (
                          <Bar key={cause} dataKey={cause} fill={CAUSE_COLORS[i % CAUSE_COLORS.length]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Cause detail table */}
            {causeBySeasonData && (
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>Cause Counts by Season</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Cause</strong></TableCell>
                        {SEASON_ORDER.filter((s) => causeBySeasonData[s]).map((s) => (
                          <TableCell key={s} align="right"><strong>{SEASON_LABELS[s]}</strong></TableCell>
                        ))}
                        <TableCell align="right"><strong>Total</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {causePieData.map((row) => {
                        const total = SEASON_ORDER.reduce((sum, s) => sum + (causeBySeasonData[s]?.[row.name] || 0), 0);
                        return (
                          <TableRow key={row.name}>
                            <TableCell>{row.name}</TableCell>
                            {SEASON_ORDER.filter((s) => causeBySeasonData[s]).map((s) => (
                              <TableCell key={s} align="right">{causeBySeasonData[s]?.[row.name] || 0}</TableCell>
                            ))}
                            <TableCell align="right"><strong>{total}</strong></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </TabPanel>

      {/* ── TAB 4: FIREFIGHTERS ─────────────────────────────────────────── */}
      <TabPanel value={activeTab} index={4}>
        {firefighterLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : firefighterList.length === 0 ? (
          <Alert severity="info">
            No firefighter or volunteer attendance recorded for the selected period. Add firefighters to
            wildfire reports to populate this tab.
          </Alert>
        ) : (
          <>
            {/* Top-N attendance bar chart */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Top Firefighters by Incidents Attended</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The {Math.min(FF_TOP_N, firefighterList.length)} most-active firefighters and volunteers across the selected filter scope.
              </Typography>
              <ResponsiveContainer width="100%" height={Math.max(280, topFirefighters.length * 34)}>
                <BarChart layout="vertical" data={topFirefighters} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={150} />
                  <Tooltip formatter={(v) => [v, 'Incidents']} />
                  <Bar dataKey="incidents" name="Incidents" fill="#e64a19" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>

            {/* Full leaderboard */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>Firefighter Leaderboard</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Attendance and activity per firefighter/volunteer. Area is the total burnt across the fires each
                person attended; the last column counts attended fires that had one or more injuries.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Firefighter</strong></TableCell>
                      <TableCell align="right"><strong>Incidents</strong></TableCell>
                      <TableCell align="right"><strong>Area (ha)</strong></TableCell>
                      <TableCell align="right"><strong>Fires w/ injuries</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {firefighterList.map((f) => (
                      <TableRow key={f.key}>
                        <TableCell>
                          {f.name || 'Unknown'}
                          {f.is_guest && (
                            <Chip label="guest" size="small" variant="outlined" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{f.incidents}</TableCell>
                        <TableCell align="right">{(f.area_ha ?? 0).toLocaleString()}</TableCell>
                        <TableCell align="right">{f.injury_fires}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </TabPanel>

      {/* Mutual aid — the two directions of the same relationship. */}
      <TabPanel value={activeTab} index={5}>
        {aidLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            {/* ── Assistance received ─────────────────────────────────── */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Assistance Received</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Who turned out to help on your fires. These figures are tracked
                separately from your own crew, so they never move your
                people-involved or man-hour totals.
              </Typography>

              {aidProviders.length === 0 ? (
                <Alert severity="info">
                  No mutual aid recorded for the selected period. Add assisting
                  organisations on the Mutual aid step of a fire report.
                </Alert>
              ) : (
                <>
                  {aidTotals && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>{aidTotals.providers}</strong> organisations ·{' '}
                      <strong>{aidTotals.incidents}</strong> attendances ·{' '}
                      <strong>{aidTotals.people}</strong> people ·{' '}
                      <strong>{aidTotals.vehicles}</strong> vehicles ·{' '}
                      <strong>{aidTotals.hours}</strong> hours
                    </Typography>
                  )}
                  <ResponsiveContainer width="100%" height={Math.max(280, topAidProviders.length * 34)}>
                    <BarChart layout="vertical" data={topAidProviders} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={170} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="people" name="People" fill="#e64a19" />
                      <Bar dataKey="vehicles" name="Vehicles" fill="#2e7d32" />
                    </BarChart>
                  </ResponsiveContainer>

                  <TableContainer sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Organisation</strong></TableCell>
                          <TableCell align="right"><strong>Fires</strong></TableCell>
                          <TableCell align="right"><strong>People</strong></TableCell>
                          <TableCell align="right"><strong>Vehicles</strong></TableCell>
                          <TableCell align="right"><strong>Hours</strong></TableCell>
                          <TableCell align="right"><strong>Area (ha)</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {aidProviders.map((p) => (
                          <TableRow key={p.provider_key}>
                            <TableCell>
                              {p.name || 'Unknown'}
                              <Chip
                                label={p.kind === 'fpa' ? 'FPA' : 'Organisation'}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                              {(p.stations || []).length > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {p.stations.map((s) => `${s.name} (${s.incidents})`).join(', ')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{p.incidents}</TableCell>
                            <TableCell align="right">{p.people}</TableCell>
                            <TableCell align="right">{p.vehicles}</TableCell>
                            <TableCell align="right">{p.hours}</TableCell>
                            <TableCell align="right">{(p.area_ha ?? 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Paper>

            {/* ── Aid we provided ─────────────────────────────────────── */}
            <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>Aid We Provided</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Fires belonging to other FPAs that your crew turned out to,
                as recorded by the FPA that logged them. You see what you
                contributed and roughly where — not their report.
              </Typography>

              {!aidProvided ? (
                <Alert severity="info">
                  Pick a single FPA in the top bar to see the aid it provided to
                  others.
                </Alert>
              ) : aidReceipts.length === 0 ? (
                <Alert severity="info">
                  Nothing recorded for the selected period. This fills in when
                  another FPA credits you on one of their reports.
                </Alert>
              ) : (
                <>
                  {providedTotals && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>{providedTotals.incidents}</strong> fires ·{' '}
                      <strong>{providedTotals.people}</strong> people ·{' '}
                      <strong>{providedTotals.vehicles}</strong> vehicles ·{' '}
                      <strong>{providedTotals.hours}</strong> hours
                    </Typography>
                  )}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>FPA</strong></TableCell>
                          <TableCell><strong>Reference</strong></TableCell>
                          <TableCell><strong>Suburb</strong></TableCell>
                          <TableCell align="right"><strong>Area (ha)</strong></TableCell>
                          <TableCell align="right"><strong>We sent</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {aidReceipts.map((r, idx) => (
                          <TableRow key={`${r.wildfire_id}-${idx}`}>
                            <TableCell>
                              {r.start_time
                                ? new Date(r.start_time).toLocaleDateString()
                                : '—'}
                            </TableCell>
                            <TableCell>{r.requesting_tenant_name || '—'}</TableCell>
                            <TableCell>{r.fire_reference || '—'}</TableCell>
                            <TableCell>
                              {r.suburb || '—'}
                              {(() => {
                                const names =
                                  r.station_names && r.station_names.length
                                    ? r.station_names
                                    : r.station_name
                                      ? [r.station_name]
                                      : [];
                                return names.length ? (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    from {names.join(', ')}
                                  </Typography>
                                ) : null;
                              })()}
                            </TableCell>
                            <TableCell align="right">
                              {(r.area_size_hectares ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {[
                                r.people ? `${r.people}p` : null,
                                r.vehicles ? `${r.vehicles}v` : null,
                                r.hours ? `${r.hours}h` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Paper>
          </>
        )}
      </TabPanel>
    </Container>
  );
};

export default StatsPage;
